import { AfterViewInit, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { SharedModule } from '../shared/shared.module';
import { HeaderComponent } from './header/header.component';
import { MainComponent } from './main/main.component';
import {
  IAppDashboard,
  IAppDashboardMenu,
  IPlugin,
  IUser,
  ModelService,
	IMenuItem,
	PluginInjectorApplicationPlugin,
	IModuleWidget,
	PluginInjectorService
} from 'core-client/api';

@Component({
    selector: 'app-dashboard',
    imports: [SharedModule, HeaderComponent, MainComponent],
    templateUrl: './dashboard.component.html',
    styleUrl: './dashboard.component.scss'
})
export class DashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly ngUnsubscribe$: Subject<void> = new Subject<void>()
  isMobileView: boolean = false;
  loggedUser!: IUser
  private dashboardMenuItems: IAppDashboardMenu[] = []

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.checkIfMobileView();
  }

  constructor(
      private readonly modelService: ModelService,
      private readonly pluginInjectorService: PluginInjectorService
  ) {}

  async ngOnInit(): Promise<void> {
    this.loggedUser = this.modelService._user.getUser()
    this.checkIfMobileView();
  }

  ngAfterViewInit(): void {
    this.modelService._user.observeUser$()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(async (user: IUser) => {
        if (this.loggedUser._id === user._id) {
          this.pluginInjectorService.widgetService.resetModuleWidgets();
          this.loggedUser = user;
					await this.addAppGlobalSettings();
          await this.loadRegisteredPlugins();
          await this.updateMenuItemsOfDashboards(
              this.dashboardMenuItems,
              user.app_dashboards
          );
          this.loadActiveDashboard(user);
        }
      })
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.onResize);
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  // methods
  private checkIfMobileView(): void {
    this.isMobileView = window.innerWidth <= 768;
  }

	private async addAppGlobalSettings(): Promise<void> {
		try {
			await this.modelService._appGlobalSettings.initialize();
		} catch (error) {
			console.error('Failed to initialize global settings:', error);
		}
	}

  private async loadRegisteredPlugins(): Promise<void> {
    const plugins = this.pluginInjectorService.getPlugins();
    for (const registerPlugin of plugins) {
      const dashboardMenuItems = this.getAppDashboardMenu(registerPlugin);
      if (dashboardMenuItems) {
        this.dashboardMenuItems = [...this.dashboardMenuItems, ...dashboardMenuItems];
      }

      this.addDynamicWidgetComponents(registerPlugin);
      if (registerPlugin.enabled) {
        this.notifyObservingPlugins(registerPlugin);
      }
    }
  }

  private loadActiveDashboard(user: IUser): void {
    for (const dashboard of user.app_dashboards) {
      if (dashboard._id === user.active_dashboard) {
        this.modelService._appDashboard.setActiveAppDashboard(dashboard);
      }
    }
  }

  private addDynamicWidgetComponents(registerPlugin: PluginInjectorApplicationPlugin.IApplicationPlugin): void {
    registerPlugin.modules?.forEach((pluginModule: PluginInjectorApplicationPlugin.IPluginModule) => {
      if (pluginModule.widgets) {
        pluginModule.widgets.forEach((modulWidget: IModuleWidget) => {
          this.pluginInjectorService.widgetService.addModuleWidget(modulWidget);
        })
      }
    })
  }

  private notifyObservingPlugins(plugin: IPlugin): void {
    this.modelService._plugin.notifyPlugin(plugin, 'activate');
  }

  private getAppDashboardMenu(registerPlugin: PluginInjectorApplicationPlugin.IApplicationPlugin): IAppDashboardMenu[] | undefined {
    return registerPlugin.menuItems
      ?.filter((menuItem: IMenuItem) => menuItem.strictMode)
      .map((menuItem: IMenuItem): IAppDashboardMenu => {
        return {
          nameIdentifier: menuItem.nameIdentifier,
          primary: {
            enabled: menuItem.primary.enabled,
            position: menuItem.primary.position
          },
          secondary: {
            enabled: menuItem.secondary.enabled,
            position: menuItem.secondary.position
          },
          pluginName: menuItem.pluginName
        }
      });
  }

  private async updateMenuItemsOfDashboards(
      pluginDashboardMenuItems: IAppDashboardMenu[],
      appDashboards: IAppDashboard[]
  ): Promise<void> {
    for (const dashboard of appDashboards) {
      let needsSave: boolean = false;

      for (const pluginDashboardMenuItem of pluginDashboardMenuItems) {
        const existingIndex = dashboard.menu.findIndex((menuItem: IAppDashboardMenu): boolean =>
            menuItem.nameIdentifier === pluginDashboardMenuItem.nameIdentifier
        );

        if (existingIndex === -1) {
          // New item: seed it into the dashboard with plugin defaults.
          needsSave = true;
          dashboard.menu.push(pluginDashboardMenuItem);
        }
        // Existing item: preserve the user's saved placement and position — do not overwrite.
      }

      if (needsSave) {
        await this.modelService._appDashboard.upsertOne(dashboard);
      }
    }
  }
}
